import React, { FC, memo, useCallback, useState } from 'react';
import classNames from 'classnames';
import { isString } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Constants
import { l } from 'constants/common';
// Import UI Components
import { Icon, Popover } from '@geneui/components';
// Import Styles
import styles from './blockedBySubscriptionInfo.module.scss';

export interface BlockedBySubscriptionChildrenProps {
    popoverActions?: {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
    };
}

interface Props {
    children: React.ReactElement;
    isBlocked?: boolean;
    align?: 'start' | 'center' | 'end';
    position?: 'top' | 'bottom' | 'left' | 'right';
}

const BlockedBySubscriptionInfo: FC<Props> = ({ children, isBlocked = true, align = 'center', position = 'top' }) => {
    const { t } = useTranslation();
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);

    const onMouseEnter = useCallback(() => {
        setIsPopoverVisible(true);
    }, []);

    const onMouseLeave = useCallback(() => {
        setIsPopoverVisible(false);
    }, []);

    if (!isBlocked) {
        return children;
    }

    return (
        <Popover
            className={styles.container}
            isOpen={isPopoverVisible}
            align={align}
            position={position}
            scrollbarNeeded={false}
            disableReposition={false}
            extendTargetWidth={false}
            Content={
                <div className={styles.content}>
                    <Icon type="bc-icon-info-fill" className={styles.icon} />
                    <p className={styles.text}>{t(l.TheFeatureIsNotIncludedInYourPackage)}</p>
                </div>
            }
        >
            {children &&
                React.Children.map(children, (child) =>
                    React.isValidElement(child)
                        ? React.cloneElement(child, {
                              className: classNames(child.props.className, styles.disableCursorPointer),
                              ...(isString(child.type)
                                  ? {
                                        onMouseEnter,
                                        onMouseLeave,
                                    }
                                  : {
                                        // NOTE: this is to add mouse events only to non-DOM elements
                                        popoverActions: {
                                            onMouseEnter,
                                            onMouseLeave,
                                        },
                                    }),
                          } as AnyObject)
                        : child,
                )}
        </Popover>
    );
};

export default memo(BlockedBySubscriptionInfo);
